import React, { useCallback, useEffect, useRef, useState } from "react";
import "./StepList.scoped.scss";
import { IStep, ISWTypeProperties, StepComponentTypes, StepTypes, SWTypes } from "interfaces/sw/SWInterfaces";
import { Nestable, Item } from "./Nestable";
import { cloneDeep } from "lodash";
import dragIcon from "media/icons/dls/drag.svg";
import arrowRight from "media/icons/dls/arrow-right-3.svg";
import { findStep } from "store/manageSW/manageSWHelpers";
import { useDispatch } from "react-redux";
import { updateStepSortOrder } from "store/manageSW/manageSWActions";
import useSelector from "store/useSelector";
import { showErrorToast } from "../../../../store/toast/toastActions";

interface IStepListProps {
  steps: IStep[],
  ancestors: IStep[],
  isDisabled: boolean,
  swTypeProperties: ISWTypeProperties,
  onStepClick(ancestorGuids: string[], stepGuid: string, stepId?: number): void,
  onStepToggle(stepGuid: string, isOpen: boolean, stepId?: number): void,
}

const StepList: React.FC<IStepListProps> = ({
  steps,
  ancestors,
  isDisabled,
  swTypeProperties,
  onStepToggle,
  onStepClick,
}) => {
  const dispatch = useDispatch();
  const parentOrders = ancestors.map(st => st.sortOrder);
  const [sortables, setSortables] = useState<Item[]>([]);
  const treeViewError = useRef("");
  const [prevItems, setPrevItems] = useState<Item[]>([]);
  const stepLevelMapping = [StepTypes.Task, StepTypes.Step, StepTypes.SubStep]

  const {
    manageSW: {
      stepVisibilities,
      enableFormulaComponentValue,
      SW: { type : SWType }
    },
  } = useSelector(store => store);

  useEffect(() => {
    setSortables(cloneDeep(steps.map((item, pIndex) => ({
      id: item.id !== undefined ? item.id.toString() : item.guid,
      text: item.title,
      hasUnResolvedComments: item.hasUnResolvedComments,
      isTask: item.isTask,
      stepType: item.stepType,
      sortOrder: item.sortOrder,
      parentGuid: item.parentGuid,
      parentId: null,
      stepId: item.id,
      isInTask: false,
      isRCTask: item.isRCTask,
      stepNumber: item.stepNumber,
      rcContainer: item.rcContainer,
      isRCStep: item.isRCStep,
      hasMasterStep: (item.masterStepId != null && item.isRCStep),
      depth: item.depth,
      collapsed: item.collapsed,
      children: item.children.map((child, cIndex) => ({
        id: child.id !== undefined ? child.id.toString() : child.guid,
        text: child.title,
        nestedIn: item.guid,
        hasUnResolvedComments: child.hasUnResolvedComments,
        isTask: child.isTask,
        stepType: child.stepType,
        isInTask: item.isTask,
        hasMasterStep: (child.masterStepId != null && child.isRCStep),
        parentIndex: (pIndex + 1).toString() + ".",
        sortOrder: child.sortOrder,
        parentGuid: child.parentGuid,
        parentId: item.id,
        stepId: child.id,
        rcContainer: child.rcContainer,
        stepNumber: child.stepNumber,
        isRCStep: child.isRCStep,
        depth: child.depth,
        collapsed: child.collapsed,
        children: child.children.map((grandchild, gIndex) => ({
          id: grandchild.id !== undefined ? grandchild.id.toString() : grandchild.guid,
          text: grandchild.title,
          nestedIn: child.guid,
          hasUnResolvedComments: grandchild.hasUnResolvedComments,
          isTask: false,
          isInTask: item.isTask,
          stepType: grandchild.stepType,
          hasMasterStep: (grandchild.masterStepId != null && grandchild.isRCStep),
          parentIndex: (pIndex + 1).toString() + "." + (cIndex + 1).toString() + ".",
          parentGuid: grandchild.parentGuid,
          sortOrder: grandchild.sortOrder,
          stepId: grandchild.id,
          parentId: child.id,
          stepNumber: grandchild.stepNumber,
          rcContainer: grandchild.rcContainer,
          isRCStep: grandchild.isRCStep,
          depth: grandchild.depth,
          collapsed: grandchild.collapsed,
          children: grandchild.children.map((gGrandChild, ggIndex) => ({
            id: gGrandChild.id !== undefined ? gGrandChild.id.toString() : gGrandChild.guid,
            text: gGrandChild.title,
            nestedIn: grandchild.guid,
            hasUnResolvedComments: gGrandChild.hasUnResolvedComments,
            isTask: false,
            isInTask: item.isTask,
            stepType: gGrandChild.stepType,
            hasMasterStep: (gGrandChild.masterStepId != null && gGrandChild.isRCStep),
            parentIndex: (pIndex + 1).toString() + "." + (cIndex + 1).toString() + "." + (gIndex + 1).toString() + ".",
            parentGuid: gGrandChild.parentGuid,
            sortOrder: gGrandChild.sortOrder,
            stepId: gGrandChild.id,
            parentId: grandchild.id,
            stepNumber: gGrandChild.stepNumber,
            isRCStep: gGrandChild.isRCStep,
            rcContainer: gGrandChild.rcContainer,
            isConditional: gGrandChild.isConditional,
            depth: gGrandChild.depth,
            collapsed: gGrandChild.collapsed,
          })),
          isConditional: grandchild.isConditional,
        })),
        isConditional: child.isConditional
      })),
      nestedIn: "",
      isConditional: item.isConditional
    }))));
  }, [steps]);

  const hasFormulas = useCallback(() => {
    return steps.some((step) =>
      step.components.some(
        (component) => component.type === StepComponentTypes.FormulaNumerical
      )
    );
  }, [steps]);

  /**
   * Calculates longer path. If result is 0 it means that there are no children.
   */
  const calculateLongestChildDistance = (draggedItem: Item, ignoreRCContainer: boolean, longestChildPath: number = 0) => {
    let currentLongestPath = 0;
    if (draggedItem?.children && draggedItem) {
      for (const element of draggedItem.children) {
        const childrenDepth = calculateLongestChildDistance(
          element, ignoreRCContainer, !ignoreRCContainer || (ignoreRCContainer && !draggedItem?.rcContainer) ? longestChildPath + 1 : longestChildPath);
        currentLongestPath = Math.max(currentLongestPath, childrenDepth);
      }
    }
    return Math.max(currentLongestPath, longestChildPath);
  }

  const onStepsChanged = (items: Item[], draggedItem: Item, targetPath: number[], maxDepth: number) => {
    const destinationParent = getTargetParent(items, targetPath, sortables);
    const revertChanges = !confirmChange(draggedItem, destinationParent, maxDepth);

    if (revertChanges) {
      if (prevItems.length > 0) {
        items = prevItems;
      } else if (sortables.length > 0) {
        items = cloneDeep(sortables);
      }
      dispatch(
        showErrorToast(
          treeViewError.current
        )
      );
      return false;
    }

    applyStepNumber(items);

    const clonedItems: Item[] = cloneDeep(items.map((item, pIndex) => {
      return {
        id: item.id,
        text: item.text,
        nestedIn: "",
        isConditional: item.isConditional,
        hasUnResolvedComments: item.hasUnResolvedComments,
        isTask: item.isTask,
        stepType: getItemStepType(null, item),
        parentGuid: item.parentGuid,
        sortOrder: item.sortOrder,
        stepId: item.stepId,
        parentId: null,
        isInTask: item.isInTask,
        isRCTask: item.isRCTask,
        stepNumber: item.stepNumber,
        rcContainer: item.rcContainer,
        isRCStep: item.isRCStep,
        depth: getItemDepthLevel(null, item),
        collapsed: item.collapsed,
        children: item.children?.map((child: any, cIndex: number) => ({
          id: child.id,
          text: child.text,
          nestedIn: item.id,
          isConditional: child.isConditional,
          hasUnResolvedComments: child.hasUnResolvedComments,
          isTask: child.isTask,
          stepType: getItemStepType(item, child),
          isInTask: child.isInTask,
          parentIndex: (pIndex + 1).toString() + ".",
          parentGuid: child.parentGuid,
          stepId: child.stepId,
          parentId: Number(item.id),
          sortOrder: child.sortOrder,
          stepNumber: child.stepNumber,
          rcContainer: child.rcContainer,
          isRCStep: child.isRCStep,
          depth: getItemDepthLevel(item, child),
          collapsed: child.collapsed,
          children: child.children?.map((grandchild: any, gIndex: number) => ({
            id: grandchild.id,
            text: grandchild.text,
            nestedIn: child.id,
            isConditional: grandchild.isConditional,
            hasUnResolvedComments: grandchild.hasUnResolvedComments,
            isTask: grandchild.isTask,
            stepType: getItemStepType(child, grandchild),
            isInTask: grandchild.isInTask,
            isRCStep: grandchild.isRCStep,
            parentIndex: (pIndex + 1).toString() + "." + (cIndex + 1).toString() + ".",
            parentGuid: grandchild.parentGuid,
            parentId: Number(child.id),
            sortOrder: grandchild.sortOrder,
            stepId: grandchild.stepId,
            stepNumber: grandchild.stepNumber,
            depth: getItemDepthLevel(child, grandchild),
            collapsed: grandchild.collapsed,
            children: grandchild.children?.map((gGrandchild: any) => ({
              id: gGrandchild.id,
              text: gGrandchild.text,
              nestedIn: grandchild.id,
              stepId: gGrandchild.stepId,
              hasUnResolvedComments: gGrandchild.hasUnResolvedComments,
              isTask: gGrandchild.isTask,
              parentId: Number(grandchild.id),
              stepType: getItemStepType(grandchild, gGrandchild),
              isInTask: gGrandchild.isInTask,
              isRCStep: gGrandchild.isRCStep,
              parentIndex: (pIndex + 1).toString() + "." + (cIndex + 1).toString() + "." + (gIndex + 1).toString() + ".",
              stepNumber: gGrandchild.stepNumber,
              isConditional: gGrandchild.isConditional,
              depth: getItemDepthLevel(grandchild, gGrandchild),
              collapsed: gGrandchild.collapsed
            })),
          })),
        })),
      };
    }));

    setPrevItems(clonedItems);
    const newSteps: IStep[] = cloneNewStepsRecursively(clonedItems);
    if (!revertChanges) {
      dispatch(updateStepSortOrder(newSteps));
    }
    return true;
  }

  /**
   * Get the item step type. The step type will be different for normal steps after dragging. 
   * For RC Tasks/Steps/SubSteps step type doesn't not change.
   */
  const getItemStepType = (parentItem: Item | null, item: Item) => {
    let depth = getItemDepthLevel(parentItem, item);
    if (item.isRCStep || item.isRCTask || item.rcContainer) {
      return item.stepType;
    }

    if (depth === 0) {
      return swTypeProperties.rootLevelType;
    } else {
      return stepLevelMapping[stepLevelMapping.indexOf(swTypeProperties.rootLevelType) + depth];
    }
  }

  /**
   * Get the item depth level. Depth calculation uses RC container depth for RCs.
   */
  const getItemDepthLevel = (parentItem: Item | null, item: Item) => {
    let itemDepth: number = 0;
    if(parentItem?.depth !== undefined) {
      if (!item.rcContainer && parentItem != null && parentItem.rcContainer === true) {
        itemDepth = parentItem.depth;
      } else if (parentItem != null) {
        itemDepth = parentItem.depth + 1;
      }
    }
    
    return itemDepth;
  }

  const confirmChange = (dragItem: Item, destinationParent: Item | null, maxDepth: number) => {
    treeViewError.current = "";
    // cannot move the step is disabled
    if (isDisabled) {
      treeViewError.current = "Disabled steps cannot be moved.";
      return false;
    }

    // cannot move if there are formula components in the document
    if (enableFormulaComponentValue && hasFormulas()) {
      treeViewError.current = "Cannot reorder steps when Formula (Numerical) Input components are present in the SW.";
      return false;
    }

    // cannot move if destination parent has a master step (RCs have a master step)
    if (dragItem.rcContainer === undefined && (dragItem.hasMasterStep || (destinationParent?.hasMasterStep))) {
      treeViewError.current = "RC can only be moved by dragging the RC container.";
      return false;
    }

    // cannot move if destination is another RC container
    if ((destinationParent?.rcContainer && destinationParent.rcContainer) || destinationParent?.isRCStep) {
      treeViewError.current = "Cannot move content into RC.";
      return false;
    }

    // cannot move RC content at task level for MFG types
    if (dragItem?.rcContainer &&
      SWType === SWTypes.MFGSWI &&
      destinationParent == null) {
      treeViewError.current = "RC Content cannot be used at Task level in MFG.";
      return false;
    }

   // cannot move task level elements to other levels
    if (!dragItem.rcContainer ) {
      if (dragItem.isTask && destinationParent != null) {
        treeViewError.current = "Tasks can only be moved at Task level.";
        return false;
      }

      if (swTypeProperties.rootLevelType == StepTypes.Task) {
        if (dragItem.stepType === StepTypes.Step &&
          destinationParent == null) {
          treeViewError.current = "Steps can only be moved at Step/SubStep level.";
          return false;
        }

        if (dragItem.stepType === StepTypes.SubStep &&
          destinationParent == null) {
          treeViewError.current = "SubSteps can only be moved at Step/SubStep level.";
          return false;
        }
      }
    }

    // cannot move if max depth is reached
    if (!isDraggedItemAllowedAtNewDepth(dragItem, maxDepth, destinationParent)) {
      treeViewError.current = "Max level of depth reached.";
      return false;
    }

    return true;
  }

  const getTargetParent = (items: Item[], targetPath: number[], sortables: Item[]): Item | null => {
    // item is at top level and it is not RC container
    if (targetPath.length === 0) {
      return null;
    }

    // item is not at top level
    let targetParent = null;
    let currentItems: Item[] | undefined = items;
    let currentSortables: Item[] | undefined = sortables;

    for (let i = 0; i < targetPath.length - 1; i++) {
      if (currentItems && currentSortables) {
        let index = targetPath[i];
        targetParent = currentItems[index];        
        currentItems = targetParent?.children;
        currentSortables = currentSortables[index]?.children
      }
    }
    return targetParent;
  }

  /**
   * Validate if dragged item should be allowed in new depth.
   * */
  function isDraggedItemAllowedAtNewDepth(draggedItem: Item, maxAllowedDepth: number, destinationParent: Item | null, ignoreRCContainer: boolean = true) {
    const longestPathToChild = calculateLongestChildDistance(draggedItem, ignoreRCContainer);
    let newItemDepth = calculateNewItemDepth(destinationParent);
    return (newItemDepth + longestPathToChild) < maxAllowedDepth;
  }

  /**
   * Calculates the new dragged item depth.
   * */
  const calculateNewItemDepth = (destinationParent: Item | null) => {
    let newDepth = 0;
    if (destinationParent != null && destinationParent.depth !== undefined) {
      newDepth = destinationParent.depth + 1;
    } else {
      newDepth = 0;
    }
    return newDepth;
  }

  const applyStepNumber = (items: Item[], stepPath: string = "") => {
    let currentIndex = 0;
    if (items !== undefined) {
      for (let i = 0; i < items.length; i++) {
        if (!items[i].rcContainer) {
          currentIndex++;
          const currentStepPath = stepPath + currentIndex;
          items[i].stepNumber = currentStepPath;
          applyStepNumber(items[i].children ?? [], currentStepPath + ".")
        }
        const rcContainerChildren = items[i].children;
        if (items[i].rcContainer && rcContainerChildren) {
          for (let j = 0; j < rcContainerChildren.length; j++) {
            currentIndex++;
            const currentStepPath = stepPath + currentIndex;
            rcContainerChildren[j].stepNumber = currentStepPath;
            applyStepNumber(rcContainerChildren[j].children ?? [], currentStepPath + ".");
          }
        }
      }
    }
  }

  const cloneNewStepsRecursively = (items: Item[]) => {
    const newSteps: IStep[] = [];
    if (items) {
      items.forEach((item, index) => {
        let [originalStep] = findStep(item.id, steps, 0, item.stepId);
        const newStep = cloneDeep(originalStep);
        if (newStep) {
          newStep.sortOrder = index + 1;
          newStep.parentGuid = item.nestedIn || null;
          newStep.hasUnResolvedComments = item.hasUnResolvedComments;
          newStep.stepNumber = item.stepNumber;
          newStep.stepType = item.stepType;
          newStep.depth = item.depth !== undefined ? item.depth : 0;
          if (item.children) {
            newStep.children = cloneNewStepsRecursively(item.children);
          }
          newSteps.push(newStep);
        }
      });
    }
    return newSteps;
  }

  const openStep = (stepGuid: string, parentGuid?: string | null, sortOrder?: number, stepId?: number) => {

    let isOpen = stepVisibilities.find(x => x.stepId === stepId)?.isOpen;

    if (isOpen === undefined || isOpen === null) {
      isOpen = true;
    }
    else {
      isOpen = !isOpen;
    }

    onStepToggle(stepGuid, isOpen, stepId);
    onStepClick(ancestors.map(st => st.guid), stepGuid, stepId);
  }

  return (
    <ol
      className={!parentOrders.length
        ? "top-level"
        : ""
      }
    >
      {sortables.length !== 0 &&<Nestable
        items={sortables}
        /*
        maxDepth={swTypeProperties.supportedDepth + (swTypeProperties.isRCSupported ? 1 : 0)}*/
        onChange={(arg: any) => {return onStepsChanged(arg.items, arg.dragItem, arg.targetPath, swTypeProperties.supportedDepth)}}
        //collapsed={true}
        renderCollapseIcon={({ isCollapsed, id, parentGuid, sortOrder, stepId }: any) =>
          isCollapsed ? (
            <img
              className="icon-small"
              alt="drag"
              src={dragIcon}
            />
          )  : (
            <img
              className="icon-small"
              alt="drag"
              src={arrowRight}
              onClick={() => openStep(id, parentGuid, sortOrder, stepId)}
            />
          )
        }
        renderItem={({ item, collapseIcon, toggleCollapse, hasChildren }: any) => (
          <div
            key={item.id}
            className={`Nestable ${item.hasUnResolvedComments ? "unresolvedComments" : undefined} ${item.hasMasterStep ? "hasMasterStep" : undefined}`}
            onClick={() => {
              if (hasChildren && !item.collapsed) {
                toggleCollapse();
              }
              openStep(item.id, item.parentGuid, item.sortOrder, item.stepId);
            }}
          >

            {collapseIcon}{item.stepNumber ? item.stepNumber + ". " + item.text : item.text}

          </div>
        )}
        //threshold={10}
      />}
    </ol>
  );
}

export default StepList;